import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';

// Routes that should NOT be accessible on mobile native app
const BLOCKED_MOBILE_ROUTES = [
  '/',           // Landing page
  '/about',
  '/public-services',
  '/contact',
  '/blog',
];

// Hide the app splash screen (created in index.html)
function hideAppSplash() {
  const splash = document.getElementById('app-splash');
  if (splash) {
    splash.style.opacity = '0';
    splash.style.transition = 'opacity 0.2s';
    setTimeout(() => splash.remove(), 200);
  }
}

// Default redirect destinations based on user role
function getDefaultRoute(role?: string): string {
  switch (role) {
    case 'super_admin':
    case 'franchise_owner':
    case 'admin':
      return '/dashboard';
    case 'barber':
      return '/barber-queue';
    case 'client':
    case 'guest':
    default:
      return '/client-queue';
  }
}

interface MobileRouteGuardProps {
  children: React.ReactNode;
}

/**
 * MobileRouteGuard
 *
 * On native mobile apps (Capacitor), this component:
 * 1. Blocks access to landing/public pages (redirects to login or dashboard)
 * 2. Auto-redirects authenticated users to their appropriate dashboard
 * 3. Shows loading screen while checking auth on blocked routes
 *
 * On web, this component does nothing and renders children directly.
 */
export function MobileRouteGuard({ children }: MobileRouteGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [hasRedirected, setHasRedirected] = useState(false);

  const isNative = Capacitor.isNativePlatform();
  const currentPath = location.pathname;
  const isBlockedRoute = BLOCKED_MOBILE_ROUTES.includes(currentPath);

  useEffect(() => {
    // On web, hide splash immediately
    if (!isNative) {
      hideAppSplash();
      return;
    }

    // Wait for auth to load
    if (loading) return;

    // Prevent infinite redirect loops
    if (hasRedirected) return;

    if (isBlockedRoute) {
      setHasRedirected(true);

      if (user) {
        // User is logged in, redirect to appropriate dashboard
        const targetRoute = getDefaultRoute(user.role);
        console.log(`[MobileRouteGuard] Redirecting logged user from ${currentPath} to ${targetRoute}`);
        navigate(targetRoute, { replace: true });
      } else {
        // No user, redirect to login
        console.log(`[MobileRouteGuard] Redirecting to /login from ${currentPath}`);
        navigate('/login', { replace: true });
      }
      // Hide splash after redirect initiated
      hideAppSplash();
    } else {
      // Not a blocked route, hide splash
      hideAppSplash();
    }
  }, [isNative, loading, user, currentPath, navigate, hasRedirected, isBlockedRoute]);

  // Reset hasRedirected when path changes (for subsequent navigations)
  useEffect(() => {
    setHasRedirected(false);
  }, [location.pathname]);

  // On native, show loading screen while auth is loading on blocked routes
  // This prevents the landing page from flashing
  if (isNative && isBlockedRoute && loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-accent/30 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
